import React from 'react';
import { Box, Text } from 'ink';
import { ConnectionState } from '../types';
import type { McpPanelServerDetailView } from '../types';
import { useKeyHandler } from '../lib/key_handler';
import { useTheme } from '../lib/theme';
import { getConnectionStateDisplay } from './connection_state_display';
import type { McpPanelCallbacks } from './McpPanelInput';

interface McpServerDetailProps {
  panelView: McpPanelServerDetailView;
  callbacks: McpPanelCallbacks;
}

export const McpServerDetail: React.FC<McpServerDetailProps> = ({ panelView, callbacks }) => {
  const { tokens } = useTheme();
  const { server, tools, serverVersion, configSource } = panelView;
  const display = getConnectionStateDisplay(tokens, server.connectionState);
  const needsAuth = server.connectionState === ConnectionState.Authenticating && server.authUrl;
  const needsApproval = server.connectionState === ConnectionState.PendingApproval;

  useKeyHandler((event) => {
    if (event.eventType !== 'press') return;

    if (needsAuth && event.name === 'return') {
      callbacks.onAuthenticate(server.name);
      event.stopPropagation();
      return;
    }

    if (needsApproval) {
      if (event.name === 'return') {
        callbacks.onApprove(server.name);
        event.stopPropagation();
      } else if (event.name === 'r') {
        callbacks.onReject(server.name);
        event.stopPropagation();
      }
    }
  });

  return (
    <Box flexDirection="column" marginTop={1}>
      <Box gap={1}>
        <Text bold>←</Text>
        <Text bold>{server.displayName ?? server.name}</Text>
        <Text color={display.color}>
          ({display.icon} {server.connectionState})
        </Text>
      </Box>

      {/* The identifier tool calls and logs use, so a shortened display name above
          stays reconcilable with what the agent and the transcript show. */}
      {server.displayName && server.displayName !== server.name && (
        <Text dimColor>Identifier: {server.name}</Text>
      )}
      {serverVersion && <Text dimColor>Version: {serverVersion}</Text>}
      {configSource && <Text dimColor>Config: {configSource}</Text>}

      {needsAuth && (
        <Box marginTop={1} flexDirection="column">
          <Text color={tokens.text.warning} bold>
            This server requires OAuth authentication.
          </Text>
          <Text>Press Enter to open the authorization page in your browser.</Text>
        </Box>
      )}

      {needsApproval && (
        <Box marginTop={1} flexDirection="column">
          <Text color={tokens.text.warning} bold>
            This server requires your approval before connecting.
          </Text>
          <Text>Press Enter to approve, or r to reject.</Text>
        </Box>
      )}

      <Box marginTop={1} flexDirection="column">
        <Text bold>Tools ({tools.length}):</Text>
        {tools.map((tool) => (
          <Box key={tool.name} flexDirection="column" paddingLeft={1}>
            <Box gap={1}>
              <Text>•</Text>
              <Text bold>{tool.name}</Text>
            </Box>
            {tool.description && (
              <Box paddingLeft={3}>
                <Text dimColor>{tool.description}</Text>
              </Box>
            )}
          </Box>
        ))}
        {tools.length === 0 && !needsAuth && !needsApproval && (
          <Text dimColor> No tools available</Text>
        )}
      </Box>
    </Box>
  );
};
